import { FunctionComponent, useEffect, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import styles from './version.module.scss'
import { useAuth } from '@/modules/context/AuthContext'
import Maintenance from '@/container/other/maintenance'
import { GET_VERSION } from '@/modules/API/software/version'
import UpdateAvailable from '@/container/other/updateAvailable.tsx'
import Loading from '@/components/Loading/Loading'

const MAX_RETRIES: number = 3
const RETRY_DELAYS_MS: number[] = [1000, 2000, 4000]

interface VersionProps {
    minVersion: string
}

const Version: FunctionComponent<VersionProps> = ({ minVersion }: VersionProps) => {
    const { apiVersion, device, setApiVersion } = useAuth()

    const [maintenanceEnabled, setMaintenanceEnabled] = useState(false)
    const [retryCount, setRetryCount] = useState(0)

    const [getVersion, { data: dataVersion, error: errorVersion }] = useLazyQuery(GET_VERSION)

    useEffect(() => {
        if (dataVersion) {
            setApiVersion(dataVersion.version)
            setRetryCount(0)
        }
    }, [dataVersion])

    useEffect(() => {
        if (!errorVersion) return

        if (retryCount >= MAX_RETRIES) {
            setMaintenanceEnabled(true)
            return
        }

        const timeout: NodeJS.Timeout = setTimeout(() => {
            setRetryCount((count) => count + 1)
            getVersion()
        }, RETRY_DELAYS_MS[retryCount])

        return () => clearTimeout(timeout)
    }, [errorVersion, retryCount])

    useEffect(() => {
        getVersion()
    }, [minVersion, device])

    function parseVersionToNumber(version: string): number {
        const [major = '0', minor = '0', patch = '0'] = version.split('.')
        return parseInt(major) * 1_000_000 + parseInt(minor) * 1_000 + parseInt(patch)
    }

    if (maintenanceEnabled) {
        return (
            <div className={styles.fullPage}>
                <Maintenance />
            </div>
        )
    }

    if (!device || apiVersion === '') {
        return (
            <div className={styles.fullPage}>
                <Loading overall />
            </div>
        )
    }

    if (parseVersionToNumber(minVersion) >= parseVersionToNumber(apiVersion)) {
        return null
    }

    return (
        <div className={styles.fullPage}>
            <UpdateAvailable platform={device?.platform || 'ios'} />
        </div>
    )
}

export default Version
